<script lang="ts" setup>
import BaseDialog from '@/components/dialogs/BaseDialog.vue'
import SkrSelect, { type SelectItem } from '@/components/inputs/SkrSelect.vue'

const props = withDefaults(
  defineProps<{
    showUsageDropdown: boolean
    isTrialing: boolean
    loading?: boolean
  }>(),
  {
    loading: false,
  }
)

const emit = defineEmits<{
  action: [payload: Dialogs['business/DialogCreateApiKey']['contextData']]
  close: []
}>()

const { t } = useI18n()

const role = ref<APIKeyRole>('api')
const keyType = ref<APIKeyType>('demo')
const description = ref('')

const usageLabels = computed<Record<APIKeyRole, string>>(() => ({
  api: t('business.developers.api.usage.api'),
  user_api: t('business.developers.api.usage.user_api'),
}))

const usageItems = computed<SelectItem<APIKeyRole>[]>(() =>
  (['api', 'user_api'] satisfies APIKeyRole[]).map(value => ({ value, title: usageLabels.value[value] }))
)

const demoText = computed<Record<APIKeyRole, string>>(() => ({
  api: t('business.developers.api.create.dialog.demo.text.api'),
  user_api: t('business.developers.api.create.dialog.demo.text.user_api'),
}))

const prodText = computed<Record<APIKeyRole, string>>(() => ({
  api: t('business.developers.api.create.dialog.prod.text.api'),
  user_api: t('business.developers.api.create.dialog.prod.text.user_api'),
}))

const createCta = computed<Record<APIKeyRole, string>>(() => ({
  api: t('business.developers.api.create.dialog.create_cta.api'),
  user_api: t('business.developers.api.create.dialog.create_cta.user_api'),
}))

const actionText = computed(() =>
  props.showUsageDropdown ? createCta.value[role.value] : t('business.developers.api.create.dialog.create')
)

const submit = () => {
  const payload: Dialogs['business/DialogCreateApiKey']['contextData'] = {
    type: keyType.value,
    description: description.value,
  }
  if (props.showUsageDropdown) {
    payload.role = role.value
  }
  emit('action', payload)
}
</script>

<template>
  <base-dialog
    max-width="500"
    card-class="create-api-key-card"
    data-cy="create_api_key_dialog"
    :title="t('business.developers.api.create.dialog.title')"
    :action-text="actionText"
    :cancel-text="t('global.cancel')"
    :loading="loading"
    @action="submit"
    @close="emit('close')"
  >
    <div class="create-api-key">
      <section v-if="showUsageDropdown">
        <h3 class="create-api-key__label">{{ t('business.developers.api.create.dialog.usage.label') }}</h3>
        <p class="create-api-key__hint">{{ t('business.developers.api.create.dialog.usage.hint') }}</p>
        <skr-select
          v-model="role"
          class="create-api-key__usage-select"
          data-cy="api_key_usage_select"
          variant="solo"
          :items="usageItems"
        />
      </section>
      <div class="create-api-key__types-card">
        <v-radio-group v-model="keyType" class="create-api-key__types" hide-details>
          <v-radio
            value="demo"
            data-cy="api_key_type_demo"
            class="create-api-key__type"
            true-icon="custom:radio_button_active"
            false-icon="custom:radio_button_inactive"
          >
            <template #label>
              <div class="create-api-key__type-text">
                <p class="create-api-key__type-title">
                  {{ t('business.developers.api.create.dialog.demo.title') }}
                </p>
                <p class="create-api-key__type-body">{{ demoText[role] }}</p>
              </div>
            </template>
          </v-radio>
          <v-divider />
          <v-radio
            value="production"
            data-cy="api_key_type_production"
            class="create-api-key__type"
            true-icon="custom:radio_button_active"
            false-icon="custom:radio_button_inactive"
            :disabled="isTrialing"
          >
            <template #label>
              <div class="create-api-key__type-text">
                <p class="create-api-key__type-title" :class="{ 'create-api-key__type-title--disabled': isTrialing }">
                  {{ t('business.developers.api.create.dialog.prod.title') }}
                </p>
                <p class="create-api-key__type-body">{{ prodText[role] }}</p>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </div>
      <section class="create-api-key__description">
        <h3 class="create-api-key__label">
          {{ t('business.developers.api.create.dialog.description_label') }}
        </h3>
        <v-textarea
          v-model="description"
          data-cy="api_key_description_input"
          variant="filled"
          :placeholder="t('business.developers.api.description.add')"
          clearable
          clear-icon="custom:clear"
          auto-grow
          rows="2"
          maxlength="100"
          hide-details
        />
      </section>
    </div>
  </base-dialog>
</template>

<style lang="scss" scoped>
.create-api-key {
  display: flex;
  flex-direction: column;
  gap: 1.875rem;

  &__label {
    margin-bottom: 3 * $bw;
    font-size: 1.125rem;
    line-height: 1.5rem;
    font-weight: 800;
    color: $c-skribbleu;
  }

  &__hint {
    margin-bottom: 4 * $bw;
    font-size: 15px;
    line-height: 1.375rem;
    color: $c-grey-dark;
  }

  &__usage-select {
    :deep(.v-field) {
      min-height: 15 * $bw;
      padding-right: 4 * $bw;
      border-radius: 2 * $bw;
      box-shadow: 0 2px 5px rgba($c-skribbleu, 0.19);
    }

    :deep(.v-field__input) {
      padding: 11px (4 * $bw);
      min-height: 15 * $bw;
    }

    :deep(.v-select__selection-text) {
      font-size: 1.125rem;
      line-height: 1.125rem;
      font-weight: 800;
      color: $c-skribbleu;
    }

    :deep(.v-select__menu-icon) {
      width: 6 * $bw;
      height: 6 * $bw;
      font-size: 1.5rem;
    }
  }

  &__types-card {
    background-color: $c-white;
    border-radius: 3px;
    box-shadow: 0 3px 8px rgba($c-skribbleu, 0.19);
  }

  &__types {
    :deep(.v-selection-control__input) {
      align-self: baseline;
    }
  }

  &__type {
    margin: 0;
    padding: (6 * $bw) 0 (6 * $bw) (8 * $bw);
    align-items: flex-start;
    --v-selection-control-size: 24px;

    :deep(.v-icon) {
      width: 6 * $bw;
      height: 6 * $bw;
      font-size: 1.5rem;
    }
  }

  &__type-text {
    padding-left: 13px;
  }

  &__type-title {
    margin-bottom: 2 * $bw;
    font-size: 1.125rem;
    line-height: 1.5rem;
    font-weight: 800;
    color: $c-skribbleu;

    &--disabled {
      color: $c-grey-dark;
    }
  }

  &__type-body {
    margin: 0;
    font-size: 1rem;
    line-height: 1.25rem;
    color: $c-grey-dark;
  }

  &__description {
    :deep(.v-field) {
      min-height: 20 * $bw;
    }

    :deep(.v-field__overlay) {
      background-color: $c-grey-fine;
      opacity: 1;
    }

    :deep(textarea) {
      padding: 3 * $bw;
      font-size: 15px;
      line-height: 1.125rem;
      font-weight: 400;
      color: $c-grey-dark;
    }
  }
}
</style>

<style lang="scss">
// global styles because dialogs are not rendered as child components. Specificity is high, so it won't collide with other global styles
.create-api-key-card.v-card {
  > .v-card-title {
    font-weight: 800;
    line-height: 1.5rem;
  }

  > .v-card-actions {
    padding: (4 * $bw) (8 * $bw);
  }
}

.v-dialog .v-overlay__content .create-api-key-card.v-card > .v-card-text {
  padding: (6 * $bw) (8 * $bw);
}
</style>
